import {
  Component,
  ChangeDetectionStrategy,
  TemplateRef,
  inject,
  input,
  output,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

@Component({
  selector: 'ag1-button-base',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Ag1ButtonBaseComponent {
  private router = inject(Router);

  // pass in a routerLink if you'd like the button to act as a link
  // this is intended to be used with the routes that are defined in your *-routing.module.ts
  readonly routerLink = input<any[] | string>(undefined);

  // pass in queryParams if you'd like to add query parameters to the routerLink
  readonly queryParams = input<{
    [key: string]: any;
  }>(undefined);

  readonly variant = input<
    | 'basic' // used to override the button styles by passing in a "fake" variant
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'danger'
    | 'success'
  >('primary');

  readonly label = input<string>('');

  readonly position = input<'left' | 'right'>('left');

  readonly tooltip = input('');

  readonly tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  readonly tooltipTemplateRef = input<TemplateRef<any> | null>(null);

  readonly tooltipHasArrow = input<boolean>(false);

  readonly disabled = input<boolean>(undefined);

  readonly classOverrides = input<string>('');

  // used to show an active state when opening a ag1-menu
  readonly isActive = input<boolean>(false);

  readonly formatWithSentenceCase = input<boolean>(true);

  readonly clickEvent = output<Event>();

  variantClasses: string = `ag1-button--${this.variant()} `;

  constructor() {}

  onClick(event: Event): void {
    // extra layer of protection
    if (this.disabled()) {
      return;
    }

    if (this.routerLink()) {
      // treat button as a link if it has a routerLink
      this.navigateToRouterLink();
      return; // unnecessary but explicit
    }

    this.clickEvent.emit(event);
  }

  navigateToRouterLink(): void {
    // since this is a SPA, we force that the link opens in the same tab
    // if you want to open the link in a new tab, please considder the implications
    const routerLink = this.routerLink();

    const parts: Array<string | any> = Array.isArray(routerLink)
      ? routerLink
      : [routerLink];

    const navigationExtras: NavigationExtras = {};

    const queryParams = this.queryParams();
    if (queryParams) {
      navigationExtras.queryParams = queryParams;
    }

    this.router.navigate(parts, navigationExtras);
  }
}
